import { defineStore } from 'pinia';

import { Config } from '@/config';
import apiService from '@/services/api.service';
import type {
  AnswersDto,
  ChoiceDto,
  CommentSummaryDto,
  DotDto,
  DotVotingStepDto,
  FormDto,
  FormVoteDto,
  LevelChoiceDto,
  LevelDto,
  MapCategoryDto,
} from '@/services/types/api.types';
import { useI18n } from 'vue-i18n';
import { Constants } from '@/constants';
import router from '@/router';
import { useSectionsStore } from '@/stores/sections.store';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import { ProcessorType, type SavingOptions } from '@/services/types/file-uploader.types';
import { buildTransportPayload } from '@/helpers/store.helper';

const buildFormPayload = (form: FormDto) => {
  return buildTransportPayload(form, {
    stripKeys: ['context', 'user'],
    normalize: [
      (payload) => {
        if (payload.type === Constants.Form.Type.OpenQuestion || payload.type === Constants.Form.Type.Map) {
          payload.are_comments_enabled = false;
        }
      },
    ],
  });
};

export interface FormsState {
  forms: FormDto[];
  votes: FormVoteDto[];
  itemLoadingStates: Set<number>;
}

function insertOrUpdate<T>(array: T[], item: T, key: keyof T) {
  const index = array.findIndex((i) => i[key] === item[key]);
  if (index !== -1) {
    array[index] = item;
  } else {
    array.push(item);
  }
}

function getFormTypes(): any[] {
  const { t } = useI18n();
  return [
    { label: t('form.type.options.proposal'), value: Constants.Form.Type.Proposal },
    { label: t('form.type.options.question'), value: Constants.Form.Type.Question },
    { label: t('form.type.options.open_question'), value: Constants.Form.Type.OpenQuestion },
    { label: t('form.type.options.matrix'), value: Constants.Form.Type.Matrix },
    { label: t('form.type.options.map'), value: Constants.Form.Type.Map },
    { label: t('form.type.options.dot_voting'), value: Constants.Form.Type.DotVoting },
  ];
}

function getFormStatuses(): any[] {
  const { t } = useI18n();
  return [
    { label: t('form.status.options.approved'), value: Constants.Form.Status.Approved },
    { label: t('form.status.options.pending'), value: Constants.Form.Status.Pending },
    { label: t('form.status.options.refused'), value: Constants.Form.Status.Deleted },
  ];
}

function getDotVotingTypes(): any[] {
  const { t } = useI18n();
  return [
    { label: t('form.type.dotVoting.types.fixed'), value: Constants.Form.DotVoting.Type.Fixed },
    { label: t('form.type.dotVoting.types.variable'), value: Constants.Form.DotVoting.Type.Variable },
  ];
}

function getDotVotingMeasurementTypes(): any[] {
  const { t } = useI18n();
  return [
    { label: t('form.type.dotVoting.measurementTypes.points'), value: Constants.Form.DotVoting.MeasurementType.Points },
    {
      label: t('form.type.dotVoting.measurementTypes.money'),
      value: Constants.Form.DotVoting.MeasurementType.Money,
    },
  ];
}

export const useFormsStore = defineStore({
  id: 'forms',
  state: (): FormsState => ({
    forms: [],
    votes: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (formId: number) => state.itemLoadingStates.has(formId),
    getItem: (state) => (formId: number) => state.forms.find((s) => s.id === formId),
    getFormsBySectionId: (state) => (sectionId: number) => state.forms.filter((s) => s.section_id === sectionId),
    getFormTypes: (state) => getFormTypes,
    getFormStatuses: (state) => getFormStatuses,
    getDotVotingTypes: (state) => getDotVotingTypes,
    getDotVotingMeasurementTypes: (state) => getDotVotingMeasurementTypes,
    previewURL: (state) => (formId: number) => {
      const form = state.forms.find((s) => s.id === formId);
      const sectionStore = useSectionsStore();
      const sectionUrl = sectionStore.previewURL(form.section_id);
      return `${sectionUrl}&form=${formId}`;
    },
    getVotes: (state) => (formId: number) => {
      return state.votes.filter((s) => s.form_id === formId);
    },
  },
  actions: {
    async fetchForm(formId: number) {
      this.itemLoadingStates.add(formId);
      try {
        const { data } = await apiService.get<FormDto>(
          `${Config.api.v2}/forms/${formId}?include=approval,showPendingApproval`,
        );
        insertOrUpdate(this.forms, data, 'id');
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(formId);
      }
    },
    async fetchFormsBySection(sectionId: number) {
      try {
        const { data } = await apiService.get<FormDto[]>(
          `${Config.api.v2}/sections/${sectionId}/forms?include=approval,showPendingApproval`,
        );
        for (const form of data) {
          insertOrUpdate(this.forms, form, 'id');
        }
      } catch (error) {
        console.error(error);
      }
    },
    async fetchVotes(formId: number) {
      try {
        const { data } = await apiService.get<FormVoteDto[]>(`${Config.api.v2}/forms/${formId}/votes`);
        const nextVotes = data.map((vote) => ({
          ...vote,
          form_id: formId,
        }));

        const otherFormsVotes = this.votes.filter((vote) => vote.form_id !== formId);
        this.votes = [...otherFormsVotes, ...nextVotes];
      } catch (error) {
        console.error(error);
      }
    },
    createForm(sectionId: number): FormDto {
      return {
        type: null,
        status: -1,
        section_id: sectionId,
        question: { image: null, title: { en: '', fr: '' }, content: { en: '', fr: '' } },
        answers: { steps: [] },
        order_num: -1,
        attachmentsOpened: false,
        is_mandatory: 0,
      };
    },
    createProposalAnswers(): AnswersDto {
      return {
        steps: [
          {
            id: 1,
            type: 'choices',
            answerId: 1,
            choice_template_id: 2,
          },
          {
            id: 2,
            type: 'levels',
            levels: [
              {
                id: 1,
                answerId: 2,
                name: {
                  en: 'I believe',
                  fr: "j''y crois",
                },
                type: 'level',
                range: [1, 10],
                order_num: 1,
              },
              {
                id: 2,
                answerId: 3,
                name: {
                  en: 'I care',
                  fr: "j''y tiens",
                },
                type: 'level',
                range: [1, 10],
                order_num: 2,
              },
            ],
          },
        ],
      };
    },
    createQuestionAnswer(): AnswersDto {
      return {
        steps: [{ id: 1, answerId: 1, type: 'choices', maxAnswers: 1, otherChoice: false, otherChoiceLabel: null }],
      };
    },
    createOpenQuestionAnswer(): AnswersDto {
      return { steps: [{ id: 1, answerId: 1, type: 'open' }] };
    },
    createMatrixAnswer(): AnswersDto {
      return { steps: [{ id: 2, type: 'levels', level_choices: [], levels: [] }] };
    },
    createMapAnswer(): AnswersDto {
      return {
        steps: [
          {
            id: 2,
            type: 'map',
            map: {
              bounds: {
                northEast: [-75.81021306737247, 45.51820726338968],
                southWest: [-75.8720810321276, 45.49723283044301],
              },
              categories: [
                {
                  id: 1,
                  name: { en: 'First Category', fr: 'Première catégorie' },
                  color: 'blue',
                  defaultVisibility: true,
                },
              ],
              mapStyle: 'mapbox://styles/mapbox/streets-v12',
              answerId: 1,
              polygon: { type: 'FeatureCollection', features: [] },
              maxVotes: Constants.Form.Map.DefaultMaxVotes,
            },
          },
        ],
      };
    },
    createDotVotingAnswer(): AnswersDto {
      return {
        steps: [
          {
            id: 1,
            answerId: 1,
            type: 'dots',
            dot_voting_type: Constants.Form.DotVoting.Type.Fixed,
            dot_voting_measurement_type: Constants.Form.DotVoting.MeasurementType.Money,
            total_budget: 25000,
            default_cost_per_choice: 5000,
            dots: [],
          },
        ] as DotVotingStepDto[],
      };
    },
    createDotVotingChoice(): DotDto {
      return {
        title: { en: '', fr: '' },
        short_text: { en: '', fr: '' },
        long_text: { en: '', fr: '' },
        order_num: -1,
      };
    },
    createChoice(): ChoiceDto {
      return {
        text: { en: '', fr: '' },
        order_num: -1,
      };
    },
    createMapCategory(): MapCategoryDto {
      return {
        name: { en: '', fr: '' },
        color: 'blue',
        defaultVisibility: true,
      };
    },
    createMatrixRow(): LevelDto {
      return {
        id: -1,
        answerId: -1,
        name: { en: '', fr: '' },
        type: 'level',
        order_num: -1,
        fullStatistic: true,
      };
    },
    createMatrixChoice(): LevelChoiceDto {
      return {
        value: -1,
        label: { en: '', fr: '' },
      };
    },
    async saveForm(
      form: FormDto,
      options: { isTemplate: boolean; sectionId?: number; consultationId?: number } = { isTemplate: false },
    ): Promise<boolean> {
      try {
        const payload = buildFormPayload(form);
        let formId = form.id;
        if (formId) {
          await apiService.put(`${Config.api.v2}/forms/${form.id}`, payload);
        } else {
          const { data } = await apiService.post<FormDto>(`${Config.api.v2}/forms`, payload);
          formId = data.id;
          if (options.isTemplate) {
            router.push({
              name: 'form_template_details',
              params: { consultationId: options.consultationId, sectionId: options.sectionId, id: formId },
            });
          } else {
            router.push({ name: 'form_details', params: { id: formId } });
          }
        }
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async saveFormImage(formId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/forms/${formId}/image`,
        });
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async removeFormImage(formId: number): Promise<boolean> {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/image`);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async deleteForm(form: FormDto) {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${form.id}`);
        const index = this.forms.findIndex((s) => s.id === form.id);
        this.forms.splice(index, 1);
      } catch (error) {
        console.error(error);
      }
    },
    async saveChoice(choice: ChoiceDto, formId: number, stepId: number, options: SavingOptions): Promise<boolean> {
      try {
        const url = choice.id
          ? `${Config.api.v2}/forms/${formId}/steps/${stepId}/choices/${choice.id}`
          : `${Config.api.v2}/forms/${formId}/steps/${stepId}/choices`;
        await FileUploaderFactory.create(options.version).upload({
          data: choice,
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Formidable,
          url,
        });
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteChoice(choice: ChoiceDto, formId: number, stepId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/steps/${stepId}/choices/${choice.id}`);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async saveMatrixRow(row: LevelDto, formId: number, stepId: number): Promise<boolean> {
      try {
        let rowId = row.id;

        if (rowId && rowId !== -1) {
          await apiService.put(`${Config.api.v2}/forms/${formId}/steps/${stepId}/levels/${row.id}`, row);
        } else {
          const { data } = await apiService.post<LevelDto>(
            `${Config.api.v2}/forms/${formId}/steps/${stepId}/levels`,
            row,
          );
          rowId = data.id;
        }
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteMatrixRow(row: LevelDto, formId: number, stepId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/steps/${stepId}/levels/${row.id}`);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async saveMatrixChoice(levelChoice: LevelChoiceDto, formId: number, stepId: number): Promise<boolean> {
      try {
        let value = levelChoice.value;

        if (value && value !== -1) {
          await apiService.put(
            `${Config.api.v2}/forms/${formId}/steps/${stepId}/level-choices/${levelChoice.value}`,
            levelChoice,
          );
        } else {
          const { data } = await apiService.post<LevelChoiceDto>(
            `${Config.api.v2}/forms/${formId}/steps/${stepId}/level-choices`,
            levelChoice,
          );
          value = data.value;
        }
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteMatrixChoice(levelChoice: LevelChoiceDto, formId: number, stepId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/steps/${stepId}/level-choices/${levelChoice.value}`);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async approve(formId: number, moderationKey: string) {
      try {
        const data = { moderationKey };
        await apiService.post(`${Config.api.v2}/forms/${formId}/:approve`, data);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async refuse(formId: number, moderationKey: string) {
      try {
        const data = { moderationKey };
        await apiService.post(`${Config.api.v2}/forms/${formId}/:refuse`, data);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async reorderChoice(choice: [ChoiceDto], formId: number, stepId: number) {
      try {
        const data = { choice };
        await apiService.post(`${Config.api.v2}/forms/${formId}/steps/${stepId}/choices/${choice.id}/:reorder`, data);
      } catch (error) {
        console.error(error);
      }
    },
    async saveVoteSummaryEntry(formId: number, formVoteId: number, summaryId: number, text: string) {
      try {
        if (summaryId === -1) {
          await apiService.post<CommentSummaryDto>(`${Config.api.v2}/forms/${formId}/votes/${formVoteId}/summaries`, {
            text,
          });
        } else {
          await apiService.put<CommentSummaryDto>(
            `${Config.api.v2}/forms/${formId}/votes/${formVoteId}/summaries/${summaryId}`,
            { text },
          );
        }
        await this.fetchVotes(formId);
        return true;
      } catch (error) {
        console.error(error); // TODO: fetch only the vote that was modified
      }
    },
    async deleteVoteSummaryEntry(formId: number, formVoteId: number, summaryId: number): Promise<boolean> {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/votes/${formVoteId}/summaries/${summaryId}`);
        await this.fetchVotes(formId); // TODO: fetch only the vote that was modified
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async analyzeForm(formId: number, language?: string): Promise<boolean> {
      try {
        const query = language ? `?language=${language}` : '';
        await apiService.post(`${Config.api.v2}/forms/${formId}/:analyze${query}`);
        await this.fetchForm(formId); // Refresh the form to get updated insights
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async resetAnalysis(formId: number): Promise<boolean> {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/ai-insights`);
        await this.fetchForm(formId); // Refresh the form to get cleared insights
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async saveMapCategory(mapCategory: MapCategoryDto, formId: number, stepId: number): Promise<boolean> {
      try {
        let mapCategoryId = mapCategory.id;

        if (mapCategoryId) {
          await apiService.put(
            `${Config.api.v2}/forms/${formId}/steps/${stepId}/map-categories/${mapCategoryId}`,
            mapCategory,
          );
        } else {
          const { data } = await apiService.post<MapCategoryDto>(
            `${Config.api.v2}/forms/${formId}/steps/${stepId}/map-categories`,
            mapCategory,
          );
          mapCategoryId = data.id;
        }
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteMapCategory(row: MapCategoryDto, formId: number, stepId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/steps/${stepId}/map-categories/${row.id}`);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async exportFormGeoJSON(formId: number) {
      try {
        const blob = await apiService.getBlobRequest(`${Config.api.v2}/reports/geojson?formId=${formId}`);
        return blob;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    async uploadMapLayer(formId: number, stepId: number, options: SavingOptions): Promise<boolean> {
      try {
        await FileUploaderFactory.create(options.version).upload({
          file: options.file,
          method: apiService.post,
          processorType: ProcessorType.Multer,
          url: `${Config.api.v2}/forms/${formId}/steps/${stepId}/map-layers`,
        });
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteMapLayer(formId: number, stepId: number, layerId: number): Promise<boolean> {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/steps/${stepId}/map-layers/${layerId}`);
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async deleteDotRow(row: DotDto, formId: number, stepId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/forms/${formId}/steps/${stepId}/dots/${row.id}`);
        await this.fetchForm(formId);
      } catch (error) {
        console.error(error);
      }
    },
    async saveDotVotingChoice(dot: DotDto, formId: number, stepId: number, options: SavingOptions): Promise<boolean> {
      try {
        const url = dot.id
          ? `${Config.api.v2}/forms/${formId}/steps/${stepId}/dots/${dot.id}`
          : `${Config.api.v2}/forms/${formId}/steps/${stepId}/dots`;
        await FileUploaderFactory.create(options.version).upload({
          data: dot,
          image: options.image,
          method: apiService.post,
          processorType: ProcessorType.Formidable,
          url,
        });
        await this.fetchForm(formId);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async reorderDotVotingChoice(dot: DotDto, formId: number, stepId: number) {
      try {
        const data = { dot };
        await apiService.post(`${Config.api.v2}/forms/${formId}/steps/${stepId}/dots/${dot.id}/:reorder`, data);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
